import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { unionBy } from "lodash-es";
import { of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { AppError } from "../../app-error/app-error.model";
import { AUTH_ACTION_TYPE } from "../../auth/auth.action";
import { CoreState } from "../../state";
import { ResponsibleGamingConfigService } from "../responsible-gaming.config";

import { ProductBlockAction } from "./product-block.action";
import { ProductBlock } from "./product-block.model";
import { ProductBlockSelector } from "./product-block.selector";
import { ProductBlockService } from "./product-block.service";

@Injectable()
export class ProductBlockEffect {
	init$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.config.getCasinoBlockEnabled()),
			map(() => ProductBlockAction.get())
		)
	);

	get$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ProductBlockAction.get),
			switchMap(() =>
				this.service.get().pipe(
					map((products) => ProductBlockAction.getSuccess(products)),
					catchError((error: AppError) => of(ProductBlockAction.getFail(error)))
				)
			)
		)
	);

	update$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ProductBlockAction.update),
			map((payload) => payload.products),
			concatLatestFrom(() => this.store.select(this.selector.get())),
			switchMap(([modifiedProducts, products]) =>
				this.service.update(this.convertToApiFormat(products, modifiedProducts)).pipe(
					map((updatedProducts) => ProductBlockAction.updateSuccess(updatedProducts.classificationBlocks)),
					catchError((error: AppError) => of(ProductBlockAction.updateFail(error)))
				)
			)
		)
	);

	clear$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => ProductBlockAction.clear())
		)
	);

	constructor(
		private actions$: Actions,
		private config: ResponsibleGamingConfigService,
		private store: Store<CoreState>,
		private selector: ProductBlockSelector,
		private service: ProductBlockService
	) {}

	private convertToApiFormat(products: ProductBlock[], modifiedProducts: ProductBlock[]): ProductBlock[] {
		return unionBy(modifiedProducts, products, "classificationName").map((product) => ({
			classificationName: product.classificationName,
			isBlocked: product.isBlocked,
		}));
	}
}
